// Meta Pixel (Key's "Master Pixel V1", Low Key Global business) for the PUBLIC
// sales pages ONLY: /, /pricing, /start-checkout and /welcome. Never import this
// from the staff app, client portal, intake or anything that shows credit data.
//
// The app is a single-page app, so two pixel defaults are switched off:
//  - disablePushState: Meta would otherwise send a PageView on every in-app
//    navigation, including a jump from /welcome to /login and beyond;
//  - autoConfig: no automatic button-click / page-metadata collection.
// The pixel script is loaded on first use, so other pages never fetch it.
//
// Hard boundary: once the pixel exists in this document, ANY in-app navigation
// to a non-sales route becomes a full page load, which discards the pixel and
// its queue. So nothing queued (and nothing late, like a Purchase confirmation)
// can ever execute while a private route — dashboard, portal, intake, credit
// reports — is the current URL. Every send also re-checks the route.
const PIXEL_ID = "622384630380302";

const SALES_PATHS = new Set(["/pricing", "/welcome", "/start-checkout"]);
function isSalesPath(pathname: string, hostname = window.location.hostname): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (SALES_PATHS.has(p)) return true;
  return p === "/" && /^(www\.)?noexcusesdisputes\.com$/i.test(hostname);
}

// Meta receives the referrer with every event. If this document was reached
// from one of OUR private pages (an /intake/<token> link, the portal, a client
// file), that URL must never be forwarded — so the pixel only starts when the
// referrer is empty, another site, a bare origin with no path (what browsers
// send across our subdomains), or one of our sales pages.
function referrerIsSafe(): boolean {
  const ref = document.referrer;
  if (!ref) return true;
  try {
    const u = new URL(ref);
    const ours = u.hostname === window.location.hostname || /(^|\.)noexcusesdisputes\.com$/i.test(u.hostname);
    if (!ours) return true;
    if ((u.pathname === "/" || u.pathname === "") && !u.search && !u.hash) return true;
    return isSalesPath(u.pathname, u.hostname);
  } catch { return false; }
}
export function onSalesPage(): boolean {
  try { return isSalesPath(window.location.pathname); } catch { return false; }
}

// Entry isolation: the pixel may only ever run in a document that was LOADED
// on a sales URL. A document that started on the dashboard, portal or intake
// (and so has private history entries of its own) never gets the pixel, even
// if it later SPA-navigates to /pricing. Evaluated once, at app boot.
const DOCUMENT_STARTED_ON_SALES = typeof window !== "undefined" && onSalesPage() && referrerIsSafe();

/** Drop everything queued and turn the pixel into a no-op before leaving. */
function killPixel(): void {
  const f = window.fbq;
  if (f) { try { f.queue.length = 0; } catch { /* ignore */ } f.callMethod = () => {}; }
  const noop = (() => {}) as unknown as Fbq;
  window.fbq = noop; window._fbq = noop;
}

let boundaryInstalled = false;
function installBoundary(): void {
  if (boundaryInstalled) return;
  boundaryInstalled = true;
  const leave = (url: string | URL | null | undefined) => {
    if (url == null) return false;
    const next = new URL(String(url), window.location.href);
    if (isSalesPath(next.pathname)) return false;
    killPixel();
    window.location.assign(next.href); // full load: the pixel does not survive it
    return true;
  };
  const push = history.pushState.bind(history);
  const replace = history.replaceState.bind(history);
  history.pushState = (data, unused, url) => { if (!leave(url)) push(data, unused, url); };
  history.replaceState = (data, unused, url) => { if (!leave(url)) replace(data, unused, url); };
  window.addEventListener("popstate", () => { if (!onSalesPage()) { killPixel(); window.location.reload(); } });
}
// Guard from the very first moment of a sales document — before React Router
// pushes or replaces anything.
if (DOCUMENT_STARTED_ON_SALES) installBoundary();

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void; queue: unknown[]; loaded: boolean; version: string;
  push: Fbq; disablePushState?: boolean;
};
declare global { interface Window { fbq?: Fbq; _fbq?: Fbq } }

function ensurePixel(): Fbq | null {
  if (typeof window === "undefined" || !DOCUMENT_STARTED_ON_SALES || !onSalesPage()) return null;
  installBoundary();
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args); else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq; fbq.loaded = true; fbq.version = "2.0"; fbq.queue = [];
  fbq.disablePushState = true;
  window.fbq = fbq; window._fbq = fbq;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  fbq("set", "autoConfig", false, PIXEL_ID);
  fbq("init", PIXEL_ID);
  return fbq;
}

/** One PageView for the sales page that just mounted. */
export function pixelPageView(): void {
  try { ensurePixel()?.("track", "PageView"); } catch { /* tracking must never break the page */ }
}

/** A standard event (InitiateCheckout, Purchase…). eventID dedupes repeats. */
export function pixelEvent(name: string, params?: Record<string, unknown>, eventID?: string): void {
  try { ensurePixel()?.("track", name, params || {}, eventID ? { eventID } : undefined); } catch { /* never break the page */ }
}
